import {
    ApiOutlined,
    AuditOutlined,
    BankOutlined,
    CloudUploadOutlined,
    DatabaseOutlined,
    FieldTimeOutlined,
    FileDoneOutlined,
    FileSearchOutlined,
    FileTextOutlined,
    FolderOpenOutlined,
    RobotOutlined,
    StopOutlined,
    SyncOutlined,
    WarningOutlined
} from "@ant-design/icons";
import {SYNC_ROLLUP_STATES} from "../reports/elements/syncStatus.constants";

export {STORAGE_KEYS} from "./presets.storage";

/**
 * The Document Audit tab is NOT a simulation mode — it browses a real
 * member's uploaded documents. It deliberately has no `MODES` entry, so
 * every `MODES[activeTab]` lookup must stay null-safe.
 */
export const AUDIT_TAB_KEY = "audit";

/**
 * The first four tab keys are the exact `mode` values the service accepts —
 * for them the active tab IS the simulation mode, no mapping layer needed.
 * `audit` is the exception (see {@link AUDIT_TAB_KEY}).
 */
export const SIMULATOR_TABS = [
    {key: "paystub", label: "Paystub", icon: <FileTextOutlined />},
    {key: "shifts", label: "Shifts", icon: <FieldTimeOutlined />},
    {key: "direct_deposit", label: "DD Allocation", icon: <BankOutlined />},
    {key: "manual_batch", label: "Full Manual Flow", icon: <FolderOpenOutlined />},
    {key: AUDIT_TAB_KEY, label: "Document Audit", icon: <FileSearchOutlined />}
];

export const SIMULATOR_TAB_KEYS = SIMULATOR_TABS.map(tab => tab.key);

export const DEFAULT_TAB = "paystub";

/** Page description for the audit tab (`MODES` has no entry for it). */
export const AUDIT_TAB_DESCRIPTION = "Browse a real member's uploaded documents from the manual-upload intake pipeline and inspect the stored bytes, verdicts and forensics.";

/** Server-accepted document MIME types — images plus PDF (5MB/file cap). */
export const DOCUMENT_ACCEPT = Object.freeze(["image/jpeg", "image/png", "image/webp", "image/heic", "application/pdf"]);

export const MAX_FILE_SIZE_MB = 5;

/**
 * Per-mode composer/upload configuration.
 *
 * `minFiles` is OUR client-side rule, not the server's: the API accepts a
 * single file for `manual_batch` (deliberately, to demo the
 * insufficient-contiguous-paystubs rejection), but the batch view is only
 * meaningful with 2+ documents, so the Run button requires 2. Uploading 2
 * still demos the insufficient path (2 < the required 3).
 */
export const MODES = Object.freeze({
    paystub: {
        label: "Paystub",
        description: "Upload one paystub and watch the full verification pipeline: classification, extraction, SOR contradictions and the redacted member response.",
        accept: DOCUMENT_ACCEPT,
        multiple: false,
        maxFiles: 1,
        minFiles: 1,
        dropLabel: "Drop one paystub (image or PDF), or click to select",
        hint: "Author paystub history in the SOR to provoke contradiction and iterative-resubmission signals."
    },
    shifts: {
        label: "Shifts",
        description: "Upload one timesheet or shift schedule and see which shifts the model extracts, plus future-dated and out-of-period fraud signals.",
        accept: DOCUMENT_ACCEPT,
        multiple: false,
        maxFiles: 1,
        minFiles: 1,
        dropLabel: "Drop one timesheet (image or PDF), or click to select",
        hint: "Set income to salaried (annually) to demo the salaried-worker-no-shifts-required rejection."
    },
    direct_deposit: {
        label: "DD Allocation",
        description: "Upload one direct-deposit form or voided check and inspect routing/last-4 extraction plus the FI-owned-routing silent signal.",
        accept: DOCUMENT_ACCEPT,
        multiple: false,
        maxFiles: 1,
        minFiles: 1,
        dropLabel: "Drop one DD form or voided check, or click to select",
        hint: "Fill the FI override (name + routing) to demo the fi_owned_routing_match signal."
    },
    manual_batch: {
        label: "Full Manual Flow",
        description: "Upload 2-3 paystubs and run the complete manual onboarding batch: contiguity analysis, derived identity/employment/income and the would-be verification outcome.",
        accept: DOCUMENT_ACCEPT,
        multiple: true,
        maxFiles: 3,
        minFiles: 2,
        dropLabel: "Drop 2-3 paystubs (images or PDFs), or click to select",
        hint: "The server accepts a single file, but we ask for at least 2 so the batch view is meaningful. Upload 2 to demo the insufficient-contiguous-paystubs rejection (3 are required)."
    }
});

/** Client-side run lifecycle (the server only knows running/failed/completed). */
export const RUN_STATUS = Object.freeze({
    UPLOADING: "uploading",
    RUNNING: "running"
});

/** Ordered progress rows rendered by `RunProgress`. */
export const RUN_STAGES = [
    {key: "upload", label: "Uploading documents"},
    {key: "queue", label: "Validated & queued"},
    {key: "verify", label: "Claude vision verification"},
    {key: "report", label: "Compiling internal report"}
];

export const POLL_INTERVAL_MS = 2000;

/** 5-minute client watchdog — the run is abandoned client-side after this. */
export const RUN_TIMEOUT_MS = 300000;

export const VERIFY_LATENCY_NOTE = "Real Claude vision — typically 5-30s per document.";

/** Contradiction severity → pill tone (`pills.module.scss` `tone_*`). */
export const SEVERITY_BY_LEVEL = Object.freeze({
    low: {label: "LOW", tone: "info"},
    medium: {label: "MEDIUM", tone: "warn"},
    high: {label: "HIGH", tone: "danger"}
});

export const SEVERITY_FALLBACK = Object.freeze({label: "—", tone: "neutral"});

/**
 * Full fraud-signal vocabulary. `silentInProduction: true` means members never
 * see the signal — it only feeds internal fraud tickets. `self_authored_document`
 * is the exception: it causes an outright rejection the member does see.
 */
export const FRAUD_SIGNAL_META = Object.freeze({
    self_authored_document: {
        label: "Self-authored document",
        description: "The document appears to have been produced by the member (PDF producer/creator metadata, editing traces). Causes rejection — NOT silent.",
        silentInProduction: false
    },
    document_internally_inconsistent: {
        label: "Internally inconsistent",
        description: "Figures inside the document contradict each other (e.g. gross minus deductions does not equal net).",
        silentInProduction: true
    },
    iterative_resubmission: {
        label: "Iterative resubmission",
        description: "The member re-submitted a slightly edited version of a recently rejected document.",
        silentInProduction: true
    },
    fi_owned_routing_match: {
        label: "FI-owned routing match",
        description: "The routing number on the document belongs to the member's own financial institution.",
        silentInProduction: true
    },
    future_dated_shifts: {
        label: "Future-dated shifts",
        description: "One or more extracted shifts are dated in the future.",
        silentInProduction: true
    },
    shifts_outside_known_periods: {
        label: "Shifts outside known periods",
        description: "Extracted shifts fall outside every pay period known to the system of record.",
        silentInProduction: true
    },
    accepted_after_repeated_rejections: {
        label: "Accepted after repeated rejections",
        description: "The document was accepted only after several prior rejected attempts.",
        silentInProduction: true
    }
});

/** Side-effect ledger `kind` → icon / label / tone (`pills.module.scss` tones). */
export const SIDE_EFFECT_META = Object.freeze({
    "document_store.store": {icon: <CloudUploadOutlined />, label: "Document store", tone: "info"},
    "document_ai.verify": {icon: <RobotOutlined />, label: "AI verify", tone: "primary"},
    "document_ai.error": {icon: <WarningOutlined />, label: "AI error", tone: "danger"},
    "db.paystub.upsert": {icon: <DatabaseOutlined />, label: "Paystub upsert", tone: "success"},
    "db.shift.upsert": {icon: <DatabaseOutlined />, label: "Shift upsert", tone: "success"},
    "db.identity.upsert": {icon: <DatabaseOutlined />, label: "Identity upsert", tone: "success"},
    "db.employment.upsert": {icon: <DatabaseOutlined />, label: "Employment upsert", tone: "success"},
    "db.income.upsert": {icon: <DatabaseOutlined />, label: "Income upsert", tone: "success"},
    "db.settings.upsert": {icon: <DatabaseOutlined />, label: "Settings upsert", tone: "success"},
    "db.sync.update_webhook_data": {icon: <SyncOutlined />, label: "Sync webhook data", tone: "info"},
    "db.sync.update_status": {icon: <SyncOutlined />, label: "Sync status", tone: "info"},
    "db.audit_log.log": {icon: <AuditOutlined />, label: "Audit log", tone: "neutral"},
    "db.document_intake_job.insert": {icon: <FileDoneOutlined />, label: "Intake job insert", tone: "info"},
    "db.document_intake_job.processing": {icon: <SyncOutlined />, label: "Intake job processing", tone: "info"},
    "db.document_intake_job.terminal": {icon: <FileDoneOutlined />, label: "Intake job terminal", tone: "neutral"},
    "trouble_ticket.would_create": {icon: <WarningOutlined />, label: "Fraud ticket", tone: "danger"},
    "sync.cascade.skipped": {icon: <StopOutlined />, label: "Sync cascade skipped", tone: "neutral"},
    "sync.pay_frequency_confirmation.skipped": {icon: <StopOutlined />, label: "Pay-frequency confirm skipped", tone: "neutral"},
    "treasury.fetch_owner": {icon: <BankOutlined />, label: "Treasury owner fetch", tone: "info"}
});

export const SIDE_EFFECT_FALLBACK = Object.freeze({icon: <ApiOutlined />, label: "Side effect", tone: "neutral"});

/**
 * Intake-job status (+ acceptance for `completed`) → pill label/tone for the
 * Document Audit table. Tones are the `pills.module.scss` `tone_*` keys.
 * Resolution lives in `audit.model.js` (`buildJobRowModel`) so it stays a
 * pure, testable function.
 */
export const JOB_STATUS_META = Object.freeze({
    queued: {label: "QUEUED", tone: "success"},
    processing: {label: "PROCESSING", tone: "success"},
    completed_accepted: {label: "ACCEPTED", tone: "success"},
    completed_rejected: {label: "REJECTED", tone: "danger"},
    failed: {label: "FAILED", tone: "warn"}
});

export const JOB_STATUS_FALLBACK = Object.freeze({label: "—", tone: "neutral"});

/** Retention column labels — bytes in S3 vs a hash-only receipt. */
export const RETENTION_LABELS = Object.freeze({
    s3: "S3",
    none: "Hash only"
});

/** Internal rejection reason codes → human labels. */
export const REJECTION_REASON_LABELS = Object.freeze({
    doc_type_unconfirmed: "Document type not confirmed",
    self_authored_document: "Self-authored document",
    contradicted: "Contradicts system of record",
    missing_period_end: "Missing pay period end",
    missing_routing_or_last4: "Missing routing number or last 4",
    no_shifts_extracted: "No shifts extracted",
    salaried_worker_no_shifts_required: "Salaried worker — no shifts required",
    ai_error: "AI verification error",
    task_not_triggered: "Task not triggered",
    insufficient_contiguous_paystubs: "Insufficient contiguous paystubs"
});

/** What the classifier is expected to confirm per mode (`ClassificationCard`). */
export const EXPECTED_DOC_TYPE_BY_MODE = Object.freeze({
    paystub: "Paystub",
    shifts: "Timesheet / shift schedule",
    direct_deposit: "Direct-deposit form / voided check",
    manual_batch: "Paystub"
});

/** Confidence meter thresholds (0..1). */
export const CONFIDENCE_GOOD = 0.85;
export const CONFIDENCE_WARN = 0.6;

// ---------------------------------------------------------------------------
// Composer select options
// ---------------------------------------------------------------------------

export const COMPENSATION_UNIT_OPTIONS = [
    {value: "hourly", label: "Hourly"},
    {value: "annually", label: "Annually (salaried)"}
];

// Values are the ENGINE's canonical vocabulary (see clockout-service
// `_inferPayFrequency` / `_annualizeGrossPay` and the derivation helpers) —
// authoring off-vocabulary strings would feed Claude SOR values production
// rows never contain.
export const PAY_FREQUENCY_OPTIONS = [
    {value: "weekly", label: "Weekly"},
    {value: "bi-weekly", label: "Bi-weekly"},
    {value: "semi-monthly", label: "Semi-monthly"},
    {value: "monthly", label: "Monthly"}
];

export const EMPLOYMENT_STATUS_OPTIONS = [
    {value: "employed", label: "Employed"},
    {value: "terminated", label: "Terminated"}
];

export const PAYSTUB_SOURCE_OPTIONS = [
    {value: "real", label: "Real"},
    {value: "mocr", label: "MOCR"},
    {value: "inferred", label: "Inferred"}
];

export const DD_SOURCE_OPTIONS = [
    {value: "real", label: "Real"},
    {value: "mocr", label: "MOCR"}
];

export const DD_ACCOUNT_TYPE_OPTIONS = [
    {value: "checking", label: "Checking"},
    {value: "savings", label: "Savings"}
];

export const PHONE_TYPE_OPTIONS = [
    {value: "mobile", label: "Mobile"},
    {value: "home", label: "Home"},
    {value: "work", label: "Work"}
];

export const ATTEMPT_STATUS_OPTIONS = [
    {value: "completed", label: "Completed"},
    {value: "failed", label: "Failed"}
];

/** Simulated pre-existing sync row states — labels from the single sync-status mapping. */
export const SYNC_STATUS_OPTIONS = ["IN_PROGRESS", "PARTIAL", "RETRY", "FAILED"].map((value) => ({
    value,
    label: SYNC_ROLLUP_STATES[value].label
}));

export const ATTEMPT_REASON_OPTIONS = Object.entries(REJECTION_REASON_LABELS).map(([value, label]) => ({value, label}));
